/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Run activity creation, ACT-F12 item commands, paired ingest, assignment and submission commands.
 * @state-owner TanStack Query mutation state
 * @allowed-imports same module queries/model/upload helper; app environment; contracts; TanStack Query
 * @forbidden routes; components; Supabase client; Zustand server entities
 * @public-api via modules/activities/index.ts
 * @side-effects authenticated create/upload/ingest/update/dispatch/submission requests and query invalidation
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests modules/activities/api/activities.mutations.test.ts, activities.queries.test.ts, and module-contracts.test.ts
 * @security-boundary teacher assessment data to authenticated FastAPI commands
 * @data-classification assessment content and class assignment metadata
 * @trust-model backend validates ownership for every upload, exam, class, and dispatch command
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { VispoApiClient } from '@vispo/api-client';
import {
  assignExamResponseSchema,
  createdActivitySchema,
  pairedIngestStatusSchema,
  pairedIngestSubmitSchema,
} from '@vispo/contracts';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import type {
  ActivityEditorItem,
  ActivityEditorItems,
  ActivityItemCopyResult,
  ActivityItemCreationAttempt,
  ActivityItemDraft,
  ActivityItemMutationResult,
  ActivityQuestion,
  ActivitySummary,
  StudentSubmissionResult,
} from '../model/types';
import { activityKeys } from './activities.queries';
import { type UploadProgress, uploadPairedPdf } from './direct-upload';

const INGEST_POLL_INTERVAL_MS = 2_500;
const INGEST_TIMEOUT_MS = 15 * 60 * 1_000;

const activityItemCopyResponseSchema = z.object({
  copy_id: z.string().uuid(),
  state: z.enum(['queued', 'processing', 'succeeded', 'failed']),
  source_workspace_kind: z.enum(['personal', 'institution']),
  source_activity_item_id: z.string().uuid(),
  source_item_revision: z.number().int().positive(),
  target_workspace_kind: z.enum(['personal', 'institution']),
  target_activity_id: z.string().uuid(),
  target_activity_item_id: z.string().uuid().nullable().optional(),
  target_activity_revision: z.number().int().positive(),
});

const activityImageUploadResponseSchema = z.object({
  asset_id: z.string().uuid(),
  filename: z.string().min(1),
  mime_type: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  byte_size: z.number().int().positive(),
});

export interface CreateActivityInput {
  organizationId: string;
  title: string;
  classroomId: string | null;
  curriculumUnitId: string | null;
  idempotencyKey: string;
  contextId: string;
}

export function createActivityBody(input: CreateActivityInput) {
  return {
    organizationId: input.organizationId,
    title: input.title.trim(),
    classroomId: input.classroomId,
    curriculumUnitId: input.curriculumUnitId,
    idempotencyKey: input.idempotencyKey,
  };
}

export async function createActivityRequest(
  apiClient: VispoApiClient,
  input: CreateActivityInput,
): Promise<ActivitySummary> {
  const result = createdActivitySchema.parse(
    await apiClient.request<unknown>('/api/v1/activities', {
      method: 'POST',
      body: createActivityBody(input),
    }),
  );
  return {
    id: result.id,
    title: result.title,
    status: result.workflow_status === 'ready' ? 'ready' : 'review',
    questions: result.item_count,
    needsReview: 0,
    updatedLabel: '剛剛',
    classIds: result.classroom_id ? [result.classroom_id] : [],
  };
}

export interface CopyActivityItemInput {
  contextId: string;
  sourceActivityId: string;
  sourceActivityItemId: string;
  expectedSourceItemRevision: number;
  targetActivityId: string;
  expectedTargetActivityRevision: number;
  idempotencyKey: string;
}

export function activityItemCopyBody(input: CopyActivityItemInput) {
  return {
    target_activity_id: input.targetActivityId,
    expected_source_item_revision: input.expectedSourceItemRevision,
    expected_target_activity_revision: input.expectedTargetActivityRevision,
    idempotency_key: input.idempotencyKey,
  };
}

function mapActivityItemCopy(payload: unknown): ActivityItemCopyResult {
  const result = activityItemCopyResponseSchema.parse(payload);
  return {
    copyId: result.copy_id,
    state: result.state,
    sourceWorkspaceKind: result.source_workspace_kind,
    sourceActivityItemId: result.source_activity_item_id,
    sourceItemRevision: result.source_item_revision,
    targetWorkspaceKind: result.target_workspace_kind,
    targetActivityId: result.target_activity_id,
    targetActivityItemId: result.target_activity_item_id ?? undefined,
    targetActivityRevision: result.target_activity_revision,
  };
}

export async function retryActivityItemCopyRequest(
  apiClient: VispoApiClient,
  copyId: string,
): Promise<ActivityItemCopyResult> {
  return mapActivityItemCopy(
    await apiClient.request<unknown>(
      `/api/v1/activity-item-copies/${encodeURIComponent(copyId)}/retry`,
      { method: 'POST' },
    ),
  );
}

export async function createActivityItemCopyRequest(
  apiClient: VispoApiClient,
  input: CopyActivityItemInput,
): Promise<ActivityItemCopyResult> {
  return mapActivityItemCopy(
    await apiClient.request<unknown>(
      `/api/v1/activity-items/${encodeURIComponent(input.sourceActivityItemId)}/copies`,
      { method: 'POST', body: activityItemCopyBody(input) },
    ),
  );
}

const editorItemResponseSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  activity_id: z.string().uuid(),
  position: z.number().int().nonnegative(),
  item_type: z.string().min(1),
  selection_mode: z.string().nullable().optional(),
  question_content: z.record(z.string(), z.unknown()),
  options: z.array(z.record(z.string(), z.unknown())),
  answer_key: z.record(z.string(), z.unknown()).nullable().optional(),
  answer_origin: z.string().nullable().optional(),
  max_points: z.union([z.number(), z.string()]),
  requires_show_work: z.boolean(),
  revision: z.number().int().positive(),
  qa_approved_revision: z.number().int().positive().nullable().optional(),
});

const itemMutationResponseSchema = z.object({
  activity_id: z.string().uuid(),
  activity_revision: z.number().int().positive(),
  item: editorItemResponseSchema,
});

const editorItemsResponseSchema = z.object({
  organization_id: z.string().uuid(),
  activity_id: z.string().uuid(),
  activity_revision: z.number().int().positive(),
  items: z.array(editorItemResponseSchema),
});

const itemCreationAttemptResponseSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(['generate_similar', 'image_import']),
  state: z.enum(['queued', 'processing', 'succeeded', 'failed', 'stale']),
  activity_id: z.string().uuid(),
  source_item_id: z.string().uuid().nullable().optional(),
  source_item_revision: z.number().int().positive().nullable().optional(),
  anchor_item_id: z.string().uuid().nullable().optional(),
  created_item_id: z.string().uuid().nullable().optional(),
  error_code: z.string().nullable().optional(),
});

function mapEditorItem(result: z.infer<typeof editorItemResponseSchema>): ActivityEditorItem {
  return {
    id: result.id,
    organizationId: result.organization_id,
    activityId: result.activity_id,
    position: result.position,
    itemType: result.item_type,
    selectionMode: result.selection_mode ?? undefined,
    questionContent: result.question_content,
    options: result.options,
    answerKey: result.answer_key ?? undefined,
    answerOrigin: result.answer_origin ?? undefined,
    maxPoints: Number(result.max_points),
    requiresShowWork: result.requires_show_work,
    revision: result.revision,
    qaApprovedRevision: result.qa_approved_revision ?? undefined,
  };
}

function mapItemMutation(payload: unknown): ActivityItemMutationResult {
  const result = itemMutationResponseSchema.parse(payload);
  return {
    activityId: result.activity_id,
    activityRevision: result.activity_revision,
    item: mapEditorItem(result.item),
  };
}

function mapEditorItems(payload: unknown): ActivityEditorItems {
  const result = editorItemsResponseSchema.parse(payload);
  return {
    organizationId: result.organization_id,
    activityId: result.activity_id,
    activityRevision: result.activity_revision,
    items: result.items.map(mapEditorItem),
    creationAttempts: [],
  };
}

function mapCreationAttempt(payload: unknown): ActivityItemCreationAttempt {
  const result = itemCreationAttemptResponseSchema.parse(payload);
  return {
    id: result.id,
    kind: result.kind,
    state: result.state,
    activityId: result.activity_id,
    sourceItemId: result.source_item_id ?? undefined,
    sourceItemRevision: result.source_item_revision ?? undefined,
    anchorItemId: result.anchor_item_id ?? undefined,
    createdItemId: result.created_item_id ?? undefined,
    errorCode: result.error_code ?? undefined,
  };
}

interface BaseEditorCommand {
  activityId: string;
  expectedActivityRevision: number;
  idempotencyKey: string;
}

export interface CreateActivityItemInput extends BaseEditorCommand {
  afterItemId?: string;
  item: ActivityItemDraft;
}

export interface ItemRevisionInput extends BaseEditorCommand {
  itemId: string;
  expectedItemRevision: number;
}

export interface UpdateActivityItemInput extends ItemRevisionInput {
  item: ActivityItemDraft;
}

export interface ReorderActivityItemsInput extends BaseEditorCommand {
  orderedItemIds: string[];
}

export interface ImportSingleQuestionInput extends BaseEditorCommand {
  assetId: string;
  afterItemId?: string;
}

export interface UploadSingleQuestionImageInput {
  activityId: string;
  file: File;
}

export interface UploadedSingleQuestionImage {
  assetId: string;
  filename: string;
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
  byteSize: number;
}

export async function uploadSingleQuestionImageRequest(
  apiClient: VispoApiClient,
  input: UploadSingleQuestionImageInput,
): Promise<UploadedSingleQuestionImage> {
  const result = activityImageUploadResponseSchema.parse(
    await apiClient.request<unknown>(
      `/api/v1/activities/${encodeURIComponent(input.activityId)}/single-question-images`,
      {
        method: 'POST',
        body: input.file,
        headers: {
          'Content-Type': input.file.type,
          'X-File-Name': encodeURIComponent(input.file.name),
        },
      },
    ),
  );
  return {
    assetId: result.asset_id,
    filename: result.filename,
    mimeType: result.mime_type,
    byteSize: result.byte_size,
  };
}

export async function createActivityItemRequest(
  apiClient: VispoApiClient,
  input: CreateActivityItemInput,
): Promise<ActivityItemMutationResult> {
  return mapItemMutation(
    await apiClient.request<unknown>(
      `/api/v1/activities/${encodeURIComponent(input.activityId)}/items`,
      {
        method: 'POST',
        body: {
          expectedActivityRevision: input.expectedActivityRevision,
          idempotencyKey: input.idempotencyKey,
          item: input.item,
          ...(input.afterItemId ? { afterItemId: input.afterItemId } : {}),
        },
      },
    ),
  );
}

export async function updateActivityItemRequest(
  apiClient: VispoApiClient,
  input: UpdateActivityItemInput,
): Promise<ActivityItemMutationResult> {
  return mapItemMutation(
    await apiClient.request<unknown>(`/api/v1/activity-items/${encodeURIComponent(input.itemId)}`, {
      method: 'PATCH',
      body: {
        expectedActivityRevision: input.expectedActivityRevision,
        expectedItemRevision: input.expectedItemRevision,
        idempotencyKey: input.idempotencyKey,
        item: input.item,
      },
    }),
  );
}

export async function duplicateActivityItemRequest(
  apiClient: VispoApiClient,
  input: ItemRevisionInput,
): Promise<ActivityItemMutationResult> {
  return mapItemMutation(
    await apiClient.request<unknown>(
      `/api/v1/activity-items/${encodeURIComponent(input.itemId)}/duplicate`,
      {
        method: 'POST',
        body: {
          expectedActivityRevision: input.expectedActivityRevision,
          expectedItemRevision: input.expectedItemRevision,
          idempotencyKey: input.idempotencyKey,
        },
      },
    ),
  );
}

export async function reorderActivityItemsRequest(
  apiClient: VispoApiClient,
  input: ReorderActivityItemsInput,
): Promise<ActivityEditorItems> {
  return mapEditorItems(
    await apiClient.request<unknown>(
      `/api/v1/activities/${encodeURIComponent(input.activityId)}/items/reorder`,
      {
        method: 'POST',
        body: {
          expectedActivityRevision: input.expectedActivityRevision,
          idempotencyKey: input.idempotencyKey,
          orderedItemIds: input.orderedItemIds,
        },
      },
    ),
  );
}

export async function deleteActivityItemRequest(
  apiClient: VispoApiClient,
  input: ItemRevisionInput,
): Promise<void> {
  await apiClient.request<unknown>(`/api/v1/activity-items/${encodeURIComponent(input.itemId)}`, {
    method: 'DELETE',
    body: {
      expectedActivityRevision: input.expectedActivityRevision,
      expectedItemRevision: input.expectedItemRevision,
      idempotencyKey: input.idempotencyKey,
    },
  });
}

export async function generateSimilarActivityItemRequest(
  apiClient: VispoApiClient,
  input: ItemRevisionInput & { afterItemId?: string },
): Promise<ActivityItemCreationAttempt> {
  return mapCreationAttempt(
    await apiClient.request<unknown>(
      `/api/v1/activity-items/${encodeURIComponent(input.itemId)}/generate-similar`,
      {
        method: 'POST',
        body: {
          expectedActivityRevision: input.expectedActivityRevision,
          expectedItemRevision: input.expectedItemRevision,
          idempotencyKey: input.idempotencyKey,
          ...(input.afterItemId ? { afterItemId: input.afterItemId } : {}),
        },
      },
    ),
  );
}

export async function importSingleQuestionRequest(
  apiClient: VispoApiClient,
  input: ImportSingleQuestionInput,
): Promise<ActivityItemCreationAttempt> {
  return mapCreationAttempt(
    await apiClient.request<unknown>(
      `/api/v1/activities/${encodeURIComponent(input.activityId)}/single-question-imports`,
      {
        method: 'POST',
        body: {
          expectedActivityRevision: input.expectedActivityRevision,
          assetId: input.assetId,
          idempotencyKey: input.idempotencyKey,
          ...(input.afterItemId ? { afterItemId: input.afterItemId } : {}),
        },
      },
    ),
  );
}

function useEditorMutation<TInput extends BaseEditorCommand, TResult>(
  request: (apiClient: VispoApiClient, input: TInput) => Promise<TResult>,
) {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => {
      if (environment.demoMode) throw new Error('Question editing is unavailable in demo mode');
      return request(environment.apiClient, input);
    },
    onSuccess: (_result, input) =>
      queryClient.invalidateQueries({ queryKey: activityKeys.editorItems(input.activityId) }),
    onError: (error, input) => {
      if (hasHttpStatus(error, 403) || hasHttpStatus(error, 409)) {
        void queryClient.invalidateQueries({
          queryKey: activityKeys.editorItems(input.activityId),
        });
      }
    },
  });
}

export function useCreateActivityItemCopy() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CopyActivityItemInput): Promise<ActivityItemCopyResult> => {
      if (environment.demoMode) throw new Error('Copy is unavailable in demo mode');
      return createActivityItemCopyRequest(environment.apiClient, input);
    },
    onSuccess: (result, input) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.editorItems(result.targetActivityId),
      });
      queryClient.invalidateQueries({
        queryKey: activityKeys.copyTargets(input.sourceActivityItemId),
      });
      queryClient.invalidateQueries({ queryKey: activityKeys.all(input.contextId) });
    },
  });
}

export function useRetryActivityItemCopy() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (copyId: string) => retryActivityItemCopyRequest(environment.apiClient, copyId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.editorItems(result.targetActivityId),
      });
      queryClient.invalidateQueries({
        queryKey: activityKeys.copyTargets(result.sourceActivityItemId),
      });
    },
  });
}

export function useCreateActivityItem() {
  return useEditorMutation(createActivityItemRequest);
}

export function useUpdateActivityItem() {
  return useEditorMutation(updateActivityItemRequest);
}

export function useDuplicateActivityItem() {
  return useEditorMutation(duplicateActivityItemRequest);
}

export function useReorderActivityItems() {
  return useEditorMutation(reorderActivityItemsRequest);
}

export function useDeleteActivityItem() {
  return useEditorMutation(deleteActivityItemRequest);
}

export function useGenerateSimilarActivityItem() {
  return useEditorMutation(generateSimilarActivityItemRequest);
}

export function useImportSingleQuestion() {
  return useEditorMutation(importSingleQuestionRequest);
}

export function useUploadSingleQuestionImage() {
  const environment = useWorkspaceEnvironment();
  return useMutation({
    mutationFn: (input: UploadSingleQuestionImageInput) => {
      if (environment.demoMode) throw new Error('Question editing is unavailable in demo mode');
      return uploadSingleQuestionImageRequest(environment.apiClient, input);
    },
  });
}

export interface ActivityImportProgress {
  label: string;
  percent: number;
  phase: 'question' | 'answer' | 'processing';
}

export interface UploadActivityInput {
  answerFile: File;
  classId: string;
  contextId: string;
  questionFile: File;
  title: string;
  signal?: AbortSignal;
  onProgress?(progress: ActivityImportProgress): void;
}

function abortError(signal?: AbortSignal): Error {
  return signal?.reason instanceof Error
    ? signal.reason
    : new DOMException('Import cancelled', 'AbortError');
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(abortError(signal));
  return new Promise((resolve, reject) => {
    const timer = globalThis.setTimeout(resolve, ms);
    const abort = () => {
      globalThis.clearTimeout(timer);
      reject(abortError(signal));
    };
    signal?.addEventListener('abort', abort, { once: true });
  });
}

function mapUploadProgress(
  phase: 'question' | 'answer',
  progress: UploadProgress,
): ActivityImportProgress {
  const offset = phase === 'question' ? 0 : 35;
  const contribution =
    progress.phase === 'authorizing'
      ? 0
      : progress.phase === 'uploading'
        ? progress.percent * 0.3
        : 30;
  return {
    phase,
    percent: Math.min(70, Math.round(offset + contribution)),
    label:
      progress.phase === 'finalizing'
        ? phase === 'question'
          ? '正在確認題目卷…'
          : '正在確認答案卷…'
        : phase === 'question'
          ? '正在上傳題目卷…'
          : '正在上傳答案卷…',
  };
}

export function useIngestActivity() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      answerFile,
      classId,
      onProgress,
      questionFile,
      signal,
      title,
    }: UploadActivityInput): Promise<ActivitySummary> => {
      if (environment.demoMode) {
        onProgress?.({ phase: 'processing', percent: 100, label: '解析完成' });
        return {
          id: `activity-${Date.now()}`,
          title: title.trim() || questionFile.name.replace(/\.pdf$/i, ''),
          status: 'review',
          questions: 8,
          needsReview: 2,
          updatedLabel: '剛剛',
          classIds: [classId],
        };
      }

      const pairId = crypto.randomUUID();
      const questionUpload = await uploadPairedPdf(
        environment.apiClient,
        questionFile,
        { classId, pairId, pairedRole: 'question' },
        {
          signal,
          onProgress: (progress) => onProgress?.(mapUploadProgress('question', progress)),
        },
      );
      const answerUpload = await uploadPairedPdf(
        environment.apiClient,
        answerFile,
        { classId, pairId, pairedRole: 'answer' },
        {
          signal,
          onProgress: (progress) => onProgress?.(mapUploadProgress('answer', progress)),
        },
      );

      onProgress?.({ phase: 'processing', percent: 72, label: '正在辨識題目與答案…' });
      const formData = new FormData();
      formData.append('class_id', classId);
      formData.append('folder_path', '');
      formData.append('title', title.trim() || questionFile.name.replace(/\.pdf$/i, ''));
      formData.append('exam_pdf_storage_path', questionUpload.storagePath);
      formData.append('answer_pdf_storage_path', answerUpload.storagePath);
      formData.append('exam_filename', questionFile.name);
      formData.append('answer_filename', answerFile.name);
      formData.append('auto_generate_videos', 'false');
      const submit = pairedIngestSubmitSchema.parse(
        await environment.apiClient.request<unknown>('/api/activities/ingestions', {
          method: 'POST',
          body: formData,
          signal,
        }),
      );

      const startedAt = Date.now();
      while (Date.now() - startedAt <= INGEST_TIMEOUT_MS) {
        if (signal?.aborted) throw abortError(signal);
        const status = pairedIngestStatusSchema.parse(
          await environment.apiClient.request<unknown>(
            `/api/activities/ingestions/${encodeURIComponent(submit.job_id)}`,
            { signal },
          ),
        );
        if (status.status === 'failed') throw new Error(status.error || '活動解析失敗');
        if (status.result) {
          onProgress?.({ phase: 'processing', percent: 100, label: '解析完成' });
          return {
            id: status.result.exam_id,
            title: title.trim() || questionFile.name.replace(/\.pdf$/i, ''),
            status: status.review_required ? 'review' : 'ready',
            questions: status.result.question_count,
            needsReview: status.review_required
              ? Math.max(1, status.result.review_reasons.length)
              : 0,
            updatedLabel: '剛剛',
            classIds: [classId],
          };
        }
        const elapsedRatio = (Date.now() - startedAt) / INGEST_TIMEOUT_MS;
        onProgress?.({
          phase: 'processing',
          percent: Math.min(96, 72 + Math.round(elapsedRatio * 24)),
          label:
            status.status === 'generating_hints'
              ? '正在建立教學提示…'
              : status.status === 'ready_for_review'
                ? '正在準備複核內容…'
                : '正在辨識題目與答案…',
        });
        await wait(INGEST_POLL_INTERVAL_MS, signal);
      }
      throw new Error('活動解析超過 15 分鐘，工作仍可能在背景繼續');
    },
    onSuccess: (activity, variables) => {
      queryClient.setQueryData<ActivitySummary[]>(
        activityKeys.all(variables.contextId),
        (current = []) => [activity, ...current.filter((item) => item.id !== activity.id)],
      );
    },
  });
}

export function useUpdateActivity() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      activityId,
      contextId: _contextId,
      questions,
      title,
    }: {
      activityId: string;
      contextId: string;
      questions: ActivityQuestion[];
      title: string;
    }) => {
      if (!environment.demoMode) {
        await environment.apiClient.request<unknown>(
          `/api/activities/${encodeURIComponent(activityId)}`,
          {
            method: 'PUT',
            body: {
              title,
              answerKey: Object.fromEntries(
                questions.map((question) => [
                  question.id,
                  {
                    ...question.answerMetadata,
                    answer: question.answer,
                    ...(question.answerMetadata &&
                    ('correctAnswer' in question.answerMetadata ||
                      'correct_answer' in question.answerMetadata)
                      ? { correctAnswer: question.answer }
                      : {}),
                    concept: question.concept,
                  },
                ]),
              ),
            },
          },
        );
      }
      return { activityId, questions, title };
    },
    onSuccess: (result, input) => {
      queryClient.setQueryData<ActivitySummary[]>(
        activityKeys.all(input.contextId),
        (current = []) =>
          current.map((activity) =>
            activity.id === result.activityId
              ? {
                  ...activity,
                  title: result.title,
                  editorQuestions: result.questions,
                  needsReview: result.questions.filter((question) => !question.reviewed).length,
                  status: result.questions.every((question) => question.reviewed)
                    ? 'ready'
                    : 'review',
                  updatedLabel: '剛剛',
                }
              : activity,
          ),
      );
    },
  });
}

export function useAssignActivity() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      activityId,
      classId,
      mode,
    }: {
      activityId: string;
      classId: string;
      mode: 'live' | 'homework';
      contextId: string;
    }) => {
      if (environment.demoMode) {
        return {
          count: 1,
          assignments: [
            {
              class_id: classId,
              classwork_item_id: 'run-live',
              attachment_id: 'paper-live',
              dispatched: 12,
            },
          ],
          mode,
        };
      }
      const result = assignExamResponseSchema.parse(
        await environment.apiClient.request<unknown>(
          `/api/activities/${encodeURIComponent(activityId)}/assignments`,
          {
            method: 'POST',
            body: {
              classIds: [classId],
              dueDate:
                mode === 'homework'
                  ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1_000).toISOString()
                  : null,
              maxAttempts: 1,
              showAnswersAfterSubmit: false,
              allowLateSubmission: mode === 'homework',
            },
          },
        ),
      );
      if (result.count === 0) throw new Error('這份活動沒有發送到任何班級');
      return { ...result, mode };
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: activityKeys.all(input.contextId) });
    },
  });
}

const studentSubmissionSchema = z
  .object({
    submission: z
      .object({
        id: z.string().min(1),
        grading_status: z.string().optional(),
      })
      .loose(),
    grading_result: z
      .object({
        grading_status: z.string().optional(),
        total_score: z.number().nullable().optional(),
        max_score: z.number().nullable().optional(),
        percentage: z.number().nullable().optional(),
      })
      .loose()
      .nullable()
      .optional(),
  })
  .loose();

export function useSubmitStudentRun() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      answers,
      assignmentId,
      examId,
      idempotencyKey,
      timeSpentSeconds,
    }: {
      answers: Record<string, string>;
      assignmentId: string;
      contextId: string;
      examId: string;
      idempotencyKey: string;
      timeSpentSeconds: number;
    }): Promise<StudentSubmissionResult> => {
      if (environment.demoMode) {
        return {
          gradingStatus: 'graded',
          maxScore: Object.keys(answers).length,
          percentage: 100,
          score: Object.keys(answers).length,
          submissionId: assignmentId,
        };
      }

      const updatedAt = new Date().toISOString();
      const response = studentSubmissionSchema.parse(
        await environment.apiClient.request<unknown>(
          `/api/students/me/runs/${encodeURIComponent(examId)}/submission`,
          {
            method: 'POST',
            body: {
              assignmentId,
              answers: Object.fromEntries(
                Object.entries(answers).map(([answerSlotId, textValue]) => [
                  answerSlotId,
                  {
                    type: 'text',
                    textValue,
                    updatedAt,
                  },
                ]),
              ),
              language: 'zh-TW',
              timeSpentSeconds,
              idempotencyKey,
            },
          },
        ),
      );
      return {
        gradingStatus:
          response.grading_result?.grading_status ??
          response.submission.grading_status ??
          'submitted',
        maxScore: response.grading_result?.max_score ?? undefined,
        percentage: response.grading_result?.percentage ?? undefined,
        score: response.grading_result?.total_score ?? undefined,
        submissionId: response.submission.id,
      };
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: activityKeys.all(input.contextId, 'student') });
      queryClient.invalidateQueries({
        queryKey: activityKeys.studentDetail(input.assignmentId),
      });
    },
  });
}

/** Kept last so the naming gate sees exactly one collection write in this hook. */
export function useCreateActivity() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateActivityInput) => {
      if (environment.demoMode) {
        await new Promise((resolve) => globalThis.setTimeout(resolve, 350));
        return {
          id: `demo-${input.idempotencyKey}`,
          title: input.title.trim(),
          status: 'review' as const,
          questions: 0,
          needsReview: 0,
          updatedLabel: '剛剛',
          classIds: input.classroomId ? [input.classroomId] : [],
        };
      }
      return createActivityRequest(environment.apiClient, input);
    },
    onSuccess: (activity, input) => {
      queryClient.setQueryData<ActivitySummary[]>(
        activityKeys.all(input.contextId, 'teacher'),
        (current = []) => [activity, ...current.filter((item) => item.id !== activity.id)],
      );
      void queryClient.invalidateQueries({
        queryKey: activityKeys.all(input.contextId, 'teacher'),
      });
    },
  });
}
